import { attr, belongsTo } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import {
  getDefaultQuestions,
  getInclusionQuestions,
  getIntialInterviewQuestionsOncoPharma,
  getSecondInterviewQuestionsOncoPharma,
  getThirdInterviewQuestionsOncoPharma
} from "parog-web/components/consultation/utils/get-consultation-questions";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";
import type ConsultationReportWrittenQuestion from "./consultation/consultation-report-written-question";

//---- Automatically generated, don't update this part ----

/**
 * Represents a report from a consultation.
 */
export default class ConsultationReportWrittenJournalEvent extends JournalEvent {
  static type = "consultation-report-written-journal-event";

  /**
   * The consultation type is a label
   */
  @attr("string") declare consultationType: string;

  /**
   * The list of PPS actions carried out
   */
  @attr() declare actionsCarriedOut: string[];

  static validations(intl: IntlService) {
    return {
      consultationType: validatePresence({
        presence: true,
        description: intl.t(
          "validations.consultation-report-written-journal-event.consultation-type"
        )
      }),
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.consultation-report-written-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée plus de 50 ans en avance. Veuillez modifier la date",
          onOrBefore: dayjs().add(50, "years").toDate()
        })
      ]
    };
  }

  //---- End of generated code ----

  @belongsTo("patient", {
    async: false,
    inverse: "consultationReportWrittenJournalEvents"
  })
  declare patient: Patient;

  @attr("consultation-report-written-questions")
  declare questions: ConsultationReportWrittenQuestion[];

  get summary() {
    return Object.assign({}, super.summary, {
      forcIdentifier: {
        feature: "consultation",
        component: "report-form"
      }
    });
  }

  get pharmaceuticalInterviewReport(): boolean {
    return (
      this.consultationType == "Entretien de suivi initial" ||
      this.consultationType ==
        "Entretien de suivi 2 - Gestion des effets indésirables" ||
      this.consultationType == "Entretien de suivi 3 - Observance"
    );
  }

  static getQuestions(type?: string): ConsultationReportWrittenQuestion[] {
    switch (type) {
      case "Entretien de suivi initial":
        return getIntialInterviewQuestionsOncoPharma();
      case "Entretien de suivi 2 - Gestion des effets indésirables":
        return getSecondInterviewQuestionsOncoPharma();
      case "Entretien de suivi 3 - Observance":
        return getThirdInterviewQuestionsOncoPharma();
      case "CR d'inclusion patient":
        return getInclusionQuestions();
      default:
        return getDefaultQuestions();
    }
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "consultation-report-written-journal-event": ConsultationReportWrittenJournalEvent;
  }
}
